import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyProfileCompletionState from './completion.state';

export interface ISop {
  id?: number | string;
  tenant_id?: number | string;
  name?: string;
  slug?: string;
  sop_file?: string;
  created_at?: string;
  updated_at?: string;
  is_published?: boolean;
  published?: boolean;
}

interface SopState {
  sops: ISop[];
  selectedSop: ISop | null;
  isLoading: boolean;
  error: unknown;

  createSop: (tenantId: string | number, payload: Partial<ISop>) => Promise<void>;
  getSops: (tenantId: string | number) => Promise<void>;
  getSopDetail: (tenantId: string | number, idOrSlug: number | string) => Promise<ISop | void>;
  updateSop: (tenantId: string | number, id: number | string, payload: Partial<ISop>) => Promise<void>;
  deleteSop: (tenantId: string | number, id: number | string) => Promise<void>;
  bulkPublishSop: (tenantId: string | number, isPublished: boolean) => Promise<void>;

  clearSops: () => void;
  setSops: (data: ISop[]) => void;
}

const mapDriveToSop = (drive: any): ISop => ({
  id: drive.id,
  tenant_id: drive.tenant_id,
  name: drive.name,
  slug: drive.slug,
  is_published: drive.is_published,
  published: drive.is_published,
  created_at: drive.created_at,
  updated_at: drive.updated_at,
  sop_file: drive.media ? JSON.stringify([drive.media]) : undefined,
});

const useSopState = create<SopState>((set, get) => ({
  sops: [],
  selectedSop: null,
  isLoading: false,
  error: null,

  setSops: data => set({ sops: data }),
  clearSops: () => set({ sops: [] }),

  createSop: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/`;
      // Map ISop payload to AddItemToDriveRequest
      const bpmPayload = {
        name: payload.name,
        drive_type: 'file',
        file_type: 'bpmn',
        is_published: payload.is_published ?? false,
      };
      await axiosInstance.post(url, bpmPayload);

      await get().getSops(tenantId);

      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('SOP created successfully');
      set({ isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getSops: async tenantId => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/sops`;
      const { data } = await axiosInstance.get(url);

      const rawContent = data.data?.content || data.data || [];
      const mappedSops = Array.isArray(rawContent) ? rawContent.map(mapDriveToSop) : [];

      set({
        sops: mappedSops,
        isLoading: false,
      });
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getSopDetail: async (tenantId, idOrSlug) => {
    set({ isLoading: true, error: null });
    try {
      let url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/file/${idOrSlug}`;

      // If idOrSlug is not a number, assume it's a slug and use the slug endpoint
      if (isNaN(Number(idOrSlug))) {
        url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/file/slug/${idOrSlug}`;
      }

      const { data } = await axiosInstance.get(url);

      const mappedSop = mapDriveToSop(data.data);
      set({ selectedSop: mappedSop, isLoading: false });
      return mappedSop;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  updateSop: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/update/file/${id}`;
      const bpmPayload = {
        name: payload.name,
        is_published: payload.is_published,
      };
      const { data } = await axiosInstance.post(url, bpmPayload);

      const mappedSop = mapDriveToSop(data.data);
      set(state => ({
        sops: state.sops.map(sop => (sop.id === id ? { ...sop, ...mappedSop } : sop)),
        selectedSop: mappedSop,
        isLoading: false,
      }));

      toast.success('SOP updated successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  deleteSop: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/delete/file/${id}`;
      await axiosInstance.delete(url);

      set(state => ({
        sops: state.sops.filter(sop => sop.id !== id),
        isLoading: false,
      }));

      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('SOP deleted successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  bulkPublishSop: async (tenantId: string | number, isPublished: boolean) => {
    // Note: BPM service might need a specific bulk publish endpoint if needed.
    // For now, we'll keep it pointing to compro or implement it in BPM.
    // Given the task is to migrate retrieval, we'll keep this as is for now or point to a future BPM endpoint.
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/probis/drive/${tenantId}/sops/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        sops: state.sops.map(ka => ({
          ...ka,
          published: isPublished,
          is_published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(isPublished ? 'All SOP published successfully' : 'All SOP unpublished successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useSopState;
